import { CircularProgress, Stack, Typography } from '@mui/material';
import {
  AddOrganisationUser,
  DisplayAlertErrors,
  OrganisationUserList,
  UserCredentialStatus,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { useOrganisationUserProvisionData } from './hooks';

export default function OrganisationUserProvision({
  organisationId,
  successNotificationHandler,
  errorNotificationHandler,
}: Readonly<
  Pick<
    Parameters<typeof useOrganisationUserProvisionData>[0],
    'successNotificationHandler' | 'errorNotificationHandler'
  > & {
    organisationId: string;
  }
>) {
  const { users, isLoading, error, addRecord, removeRecord } = useOrganisationUserProvisionData({
    organisationId,
    successNotificationHandler,
    errorNotificationHandler,
  });

  const AdditionalUserInfo = (user: (typeof users)[0]) => <UserCredentialStatus {...user} />;

  return (
    <Stack
      gap={tokens.spacingXs}
      mt={tokens.spacingBase}
      data-testid={`organisation-user-provision-${organisationId}`}
      data-component="organisation-user-provision"
      aria-busy={isLoading}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Typography variant="h5">Users</Typography>
        <AddOrganisationUser onAddUser={addRecord} />
      </Stack>
      {isLoading && <CircularProgress size={20} />}
      {error && <DisplayAlertErrors errors={[{ message: 'Failed to load user data.' }]} />}
      {!isLoading && !error && (
        <OrganisationUserList
          users={users}
          AdditionalItem={AdditionalUserInfo}
          onRemoveUser={removeRecord}
        />
      )}
    </Stack>
  );
}
